/**
 * Heater Requirements Calculator
 *
 * All thresholds and parameters are configurable via Rule Registry.
 */
import { selectHeaterStep } from './heaterSizing';
import { getRuleValue, type RuleOverrides } from '../../data/ruleRegistry';
import type { HeaterType } from '../../engines/types';
import type { Market } from '../location/types';
import { getT } from '../../i18n';
import type { HeaterModelFit } from './heaterCatalog';

export const PANORAMA_HEAT_LOSS_MULTIPLIER = 1.4;

/** Standard Swedish group-fuse sizes above the 10A base tier. */
const STANDARD_FUSE_SIZES_A = [16, 20, 25];

export interface HeaterRequirementsInput {
  volM3: number;
  totalVolumeM3?: number;
  /** Windows and glazed doors bounding the hot room, m². */
  glassAreaSqm: number;
  /**
   * Floor-to-ceiling panorama glass bounding the hot room, m². Weighted by
   * `heater.panorama.heatLossMultiplier` on top of the cold-surface factor;
   * ordinary windows in a panorama design keep the standard factor.
   */
  panoramaGlassAreaSqm?: number;
  stoneAreaSqm: number;
  isOutdoorLogCabin: boolean;
  heaterType?: HeaterType;
  hasRelaxRoom?: boolean;
}

export interface HeaterRequirementsResult {
  effectiveVolumeM3: number;
  recommendedKw: number;
  requiredFuseA: number;
  electricalRequirements: string;
  warning: string | null;
  /**
   * Set when the design names a catalog model: whether its rated room volume
   * covers this room (issue 159). Added by the enrichment chain.
   */
  modelFit?: HeaterModelFit;
}

/** Current per phase for a heater on 400 V three-phase (SS 436 40 00). */
export function heaterAmpsPerPhase(kw: number): number {
  return (kw * 1000) / (400 * Math.sqrt(3));
}

/**
 * The fuse a heater needs: 10 A up to the rule's threshold, otherwise the
 * smallest standard size that carries its current per phase. The one owner
 * of the choice — the builder, the calculator and the guides' tables read it.
 */
export function heaterFuseA(kw: number, overrides: RuleOverrides = {}): number {
  const fuseThreshold = getRuleValue<number>('heater.fuseThresholdAmps', overrides);
  const ampsPerPhase = heaterAmpsPerPhase(kw);
  return ampsPerPhase <= fuseThreshold
    ? 10
    : STANDARD_FUSE_SIZES_A.find((size) => size >= ampsPerPhase) ??
        STANDARD_FUSE_SIZES_A[STANDARD_FUSE_SIZES_A.length - 1];
}

export function calculateHeaterRequirements(
  input: HeaterRequirementsInput,
  overrides: RuleOverrides = {},
  market: Market = 'se',
): HeaterRequirementsResult {
  const t = getT(market);
  const {
    volM3,
    glassAreaSqm,
    panoramaGlassAreaSqm = 0,
    stoneAreaSqm,
    isOutdoorLogCabin,
    heaterType = 'electric',
    hasRelaxRoom,
  } = input;
  const baseVolumeM3 = hasRelaxRoom === false && input.totalVolumeM3 !== undefined
    ? input.totalVolumeM3
    : volM3;

  const coldSurfaceFactor = getRuleValue<number>('heater.coldSurfaceFactor', overrides);
  const panoramaHeatLossMultiplier = getRuleValue<number>(
    'heater.panorama.heatLossMultiplier',
    overrides,
  );
  const logCabinFactor = getRuleValue<number>('heater.logCabinFactor', overrides);
  const upfuseWarning = getRuleValue<number>('heater.upfuseWarningThreshold', overrides);
  const voltageSystem = getRuleValue<string>('heater.voltageSystem', overrides);

  let effectiveVolume =
    baseVolumeM3 +
    glassAreaSqm * coldSurfaceFactor +
    panoramaGlassAreaSqm * coldSurfaceFactor * panoramaHeatLossMultiplier +
    stoneAreaSqm * coldSurfaceFactor;
  if (isOutdoorLogCabin) {
    effectiveVolume *= logCabinFactor;
  }

  const step = selectHeaterStep(effectiveVolume, overrides);
  const largestStepKw = step.kw;
  const recommendedKw = step.kw;
  // Past the largest step the recommendation stops growing with the room.
  // Say so instead of quietly sizing a 50 m³ room at 15 kW (issue 157).
  const overLargestStepWarning =
    step.overLargestStep
      ? t.domain_heater_warningOverLargestStep
          .replaceAll(
            '{volume}',
            market === 'se' ? effectiveVolume.toFixed(1).replace('.', ',') : effectiveVolume.toFixed(1),
          )
          .replaceAll('{kw}', market === 'se' ? String(largestStepKw).replace('.', ',') : String(largestStepKw))
      : null;

  const requiredFuseA = heaterFuseA(recommendedKw, overrides);

  return {
    effectiveVolumeM3: Number(effectiveVolume.toFixed(1)),
    recommendedKw,
    requiredFuseA,
    electricalRequirements: heaterType === 'wood'
      ? t.domain_heater_elReqWood
      : // The voltage rule is Swedish admin content ("400V 3-fas"); the
        // Swedish template quotes it, the English one states it in English.
        t.domain_heater_elReqElectric
          .replace('{voltage}', voltageSystem)
          .replace('{fuse}', String(requiredFuseA)),
    warning:
      overLargestStepWarning ??
      (heaterType === 'wood'
        ? null
        : requiredFuseA > upfuseWarning
        ? t.domain_heater_warningUpfuse
        : null),
  };
}
